import React from "react";
import { Modal } from "react-bootstrap";

/**
 * CommonModal - A reusable modal component with a premium finance-style design.
 *
 * Props:
 *   show           {boolean}   Toggle modal visibility
 *   onHide         {function}  Callback triggered when modal is closed
 *   title          {node}      Modal header title
 *   children       {node}      Modal body content
 *   footer         {node}      Modal footer content (optional)
 *   size           {string}    "sm" | "lg" | "xl" (default: "lg")
 *   fullscreen     {boolean|string}
 *   centered       {boolean}   (default: true)
 *   scrollable     {boolean}   (default: true)
 *   bodyClassName  {string}    Custom classes for modal body
 *   headerClassName {string}   Custom classes for modal header
 *   footerClassName {string}   Custom classes for modal footer
 */
const CommonModal = ({
  show,
  onHide,
  title,
  children,
  footer,
  size = "lg",
  fullscreen = false,
  centered = true,
  scrollable = true,
  bodyClassName = "",
  headerClassName = "",
  footerClassName = "",
  hideClose = false,
  customHeader = null,
  ...props
}) => {
  return (
    <Modal
      show={show}
      onHide={onHide}
      size={size}
      fullscreen={fullscreen}
      centered={centered}
      scrollable={scrollable}
      className="finance-common-modal"
      {...props}
    >
      {(title || customHeader || (!hideClose && onHide)) && (
        <Modal.Header className={`border-0 px-4 pt-4 pb-2 ${headerClassName}`}>
          {customHeader ? (
            <>
              {customHeader}
              {!hideClose && onHide && (
                <button
                  type="button"
                  className="btn-close shadow-none"
                  onClick={onHide}
                  aria-label="Close"
                ></button>
              )}
            </>
          ) : (
            <>
              {title && (
                <Modal.Title className="fs-18 fw-bold text-black w-100">
                  {title}
                </Modal.Title>
              )}
              {!hideClose && onHide && (
                <button
                  type="button"
                  className="btn-close shadow-none"
                  onClick={onHide}
                  aria-label="Close"
                ></button>
              )}
            </>
          )}
        </Modal.Header>
      )}
      <Modal.Body className={`px-4 ${bodyClassName}`}>{children}</Modal.Body>
      {footer && (
        <Modal.Footer className={`border-0 px-4 pb-4 pt-2 ${footerClassName}`}>
          {footer}
        </Modal.Footer>
      )}

      <style>{`
        .finance-common-modal .modal-content {
          border-radius: 12px;
          border: none;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        .finance-common-modal .modal-header .btn-close {
          position: absolute;
          top: 15px;
          right: 20px;
          z-index: 10;
          background-size: 10px;
          padding: 1rem;
          margin: 0;
          opacity: 0.5;
          transition: opacity 0.2s;
        }

        .finance-common-modal .modal-header .btn-close:hover {
          opacity: 1;
        }

        .finance-common-modal .modal-body {
           color: #475467;
        }

        /* For fullscreen modals, we might want to adjust the radius or header */
        .finance-common-modal.modal-fullscreen .modal-content {
          border-radius: 0;
        }
      `}</style>
    </Modal>
  );
};

export default CommonModal;
